import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * <CPUParticles3D> draws Godot's CPU particle emitter as a frozen pose (`ParticleEmitter.tsx`), with
 * its box and emission-shape gizmos.
 */
import { useLayoutEffect, useRef, useState } from 'react';
import { EMPTY_AABB } from '../../../../godot/math/aabb';
import { sameTransform3D } from '../../../../godot/math/transform3d';
import { PluginGizmo, SelectedOnly } from '../../../../r3f/gizmo/PluginGizmo';
import { realGlobalTransform3D } from '../../../../r3f/nodeTreeTransforms';
import { useLazyModule } from '../../../../resources/useLazyModule';
import { Node3D } from '../../../base/node3d/Component';
import { CPUParticlesBoxGizmo, EmissionShapeGizmo } from '../gizmo/ParticleGizmos';
import { loadParticleEmitter } from './emitterModule';
export function CPUParticles3D({ node, children }) {
    const properties = node.properties;
    const nodeRef = useRef(null);
    const emitter = useLazyModule(loadParticleEmitter);
    const emissionTransform = useEmissionTransform(nodeRef, node);
    return (_jsxs(Node3D, { node: node, ref: nodeRef, children: [emitter && emissionTransform && (_jsx(emitter.ParticleEmitter, { properties: properties, nodeRef: nodeRef, emissionTransform: emissionTransform })), _jsxs(PluginGizmo, { drawnFor: "every-node", children: [_jsx(CPUParticlesBoxGizmo, { box: properties.visibilityAabb ?? EMPTY_AABB }), _jsx(SelectedOnly, { children: _jsx(EmissionShapeGizmo, { emission: properties.emission }) })] }), children] }));
}
/**
 * The emitter's global transform: a `local_coords = false` emitter emits from it, and a VIEW_DEPTH
 * emitter sorts through its inverse. three holds the ancestors, so it is sampled again for each
 * parse of the node, and changes identity only when it changes. Null until the first sample, so the emitter
 * settles once on mount, from the transform it draws under.
 */
function useEmissionTransform(nodeRef, parsed) {
    const [transform, setTransform] = useState(null);
    useLayoutEffect(() => {
        const node = nodeRef.current;
        if (!node)
            return;
        // A layout pass runs before the renderer's own `updateMatrixWorld`, so the ancestors refresh.
        node.updateWorldMatrix(true, false);
        const next = realGlobalTransform3D(node);
        setTransform((previous) => (previous && sameTransform3D(previous, next) ? previous : next));
    }, [nodeRef, parsed]);
    return transform;
}
//# sourceMappingURL=Component.js.map